// Psychological Safety Framework Constants
// Based on Amy Edmondson's research and the 4 Stages model by Timothy R. Clark

export const QUESTIONS = [
  // Inclusion Safety (Stage 1)
  { id: 1, stage: 'inclusion', text: "I feel welcome and accepted as a member of this team.", positive: true },
  { id: 2, stage: 'inclusion', text: "People on this team sometimes reject others for being different.", positive: false },
  { id: 3, stage: 'inclusion', text: "I can be myself at work without fear of judgment.", positive: true },
  
  // Learner Safety (Stage 2)
  { id: 4, stage: 'learner', text: "It is safe to take a risk and try new things in this team.", positive: true },
  { id: 5, stage: 'learner', text: "If I make a mistake in this team, it is held against me.", positive: false },
  { id: 6, stage: 'learner', text: "I feel comfortable asking questions when I don't understand something.", positive: true },
  
  // Contributor Safety (Stage 3)
  { id: 7, stage: 'contributor', text: "Members of this team are able to bring up problems and tough issues.", positive: true },
  { id: 8, stage: 'contributor', text: "My unique skills and talents are valued and utilized.", positive: true },
  { id: 9, stage: 'contributor', text: "It is difficult to ask other members of this team for help.", positive: false },
  
  // Challenger Safety (Stage 4)
  { id: 10, stage: 'challenger', text: "No one on this team would deliberately act in a way that undermines my efforts.", positive: true },
  { id: 11, stage: 'challenger', text: "I feel safe challenging ideas or decisions, even from senior team members.", positive: true },
  { id: 12, stage: 'challenger', text: "When I share a dissenting opinion, it is received with curiosity rather than defensiveness.", positive: true }
];

export const STAGES = {
  inclusion: {
    name: 'Inclusion Safety',
    color: '#2D4F4D',
    question: 'Does everyone feel they belong here?',
    description: 'The foundation — where belonging begins. Your team members feel accepted and free to be themselves.'
  },
  learner: {
    name: 'Learner Safety',
    color: '#6B9E9B',
    question: 'Can the team learn and make mistakes openly?',
    description: 'Your team feels safe to learn — asking questions, giving and receiving feedback, experimenting, and making mistakes without fear.'
  },
  contributor: {
    name: 'Contributor Safety',
    color: '#D4854A',
    question: 'Does everyone feel empowered to contribute?',
    description: 'Your team feels safe to contribute their ideas and efforts — empowered to participate as full members.'
  },
  challenger: {
    name: 'Challenger Safety',
    color: '#C07338',
    question: 'Can the team challenge ideas without fear?',
    description: 'Your team feels safe to challenge the status quo — speaking up, pushing for improvement, and disagreeing without fear of retaliation.'
  }
};

export const RECOMMENDATIONS = {
  inclusion: {
    dos: [
      "Establish a team norm of welcoming and introducing new members together",
      "Create shared rituals that bring everyone into the conversation — standups, check-ins, round-robins",
      "Rotate meeting facilitation so different voices lead the room",
      "Build team agreements about how you communicate and make space for each other",
      "Schedule informal time together so connections form naturally across the team"
    ],
    donts: [
      "Assume inclusion happens on its own — the team needs to build it intentionally",
      "Allow side conversations or cliques to become the real decision-making channels",
      "Let some voices consistently dominate while others go unheard",
      "Rely on one person to be the 'includer' — it's a shared team responsibility",
      "Ignore patterns of who speaks, who gets interrupted, and who stays silent"
    ]
  },
  learner: {
    dos: [
      "Normalise learning as a team — hold retrospectives and openly discuss what didn't work",
      "Create team habits around sharing mistakes and lessons without blame",
      "Dedicate time for the team to experiment, explore, and try new approaches together",
      "Pair up team members across experience levels so learning flows in both directions",
      "Celebrate collective learning moments — not just individual wins or successes"
    ],
    donts: [
      "Create a team culture where mistakes are hidden or punished",
      "Let the team rush past failures without reflecting on what happened",
      "Allow knowledge hoarding — information should flow freely across the team",
      "Set expectations of perfection that discourage people from trying new things",
      "Frame learning as something only junior team members need to do"
    ]
  },
  contributor: {
    dos: [
      "Build team processes that actively seek input from every member before decisions are made",
      "Distribute ownership so that everyone has meaningful responsibility — not just the loudest voices",
      "Create feedback loops where the team regularly checks in on workload, clarity, and support",
      "Recognise contributions publicly as a team habit, not just a manager's job",
      "Hold each other accountable to outcomes through shared agreements and transparency"
    ],
    donts: [
      "Let decisions happen without the team's input or awareness",
      "Concentrate responsibility on a few people while others disengage",
      "Ignore when team members are overloaded or under-utilised",
      "Allow contributions from quieter or newer members to be overlooked",
      "Treat accountability as top-down — the team should own it together"
    ]
  },
  challenger: {
    dos: [
      "Make constructive dissent a team norm — build habits like devil's advocate rotations or pre-mortems",
      "Agree as a team that challenging ideas is welcomed and expected, not a sign of conflict",
      "Create structured ways to surface concerns — anonymous input, written feedback before discussion",
      "Practise separating ideas from the people who proposed them so critique feels safe",
      "Debrief bold decisions together — reinforce that the team values candour regardless of outcome"
    ],
    donts: [
      "Let groupthink take hold — silence is not the same as agreement",
      "Allow social pressure to shut down legitimate intellectual debate",
      "Treat disagreement as disloyalty or negativity within the team",
      "Ignore the pattern of who feels safe to challenge and who doesn't",
      "Default to hierarchy or seniority when the team should be reasoning together"
    ]
  }
};

export const INDIVIDUAL_ACTIONS = {
  inclusion: {
    low: [
      "Schedule informal 1:1s with team members you don't know well",
      "Practise active listening in your next three meetings",
      "Share something personal (appropriately) in your next team interaction",
      "Reach out to someone new on the team this week",
      "Observe who speaks least in meetings and invite their perspective"
    ],
    high: [
      "Become the team's unofficial 'welcome ambassador' for new joiners",
      "Proactively introduce colleagues who should know each other but don't",
      "Share your approach to inclusion with others who may struggle",
      "Mentor someone who feels like an outsider",
      "Facilitate 'get to know you' moments in team meetings",
      "Advocate for inclusive practices in team rituals and processes",
      "Notice and call out exclusionary behaviour constructively"
    ],
    leveraging: [
      "Your sense of belonging helps others feel safe - use it to model openness",
      "You can be the 'social glue' that connects disparate team members",
      "When others feel excluded, your support can make a significant difference",
      "Use your comfort to speak up when you notice someone being left out"
    ]
  },
  learner: {
    low: [
      "Share a recent mistake and what you learned in your next meeting",
      "Ask a 'dumb question' publicly to model curiosity",
      "Request feedback on something specific you're working on",
      "Volunteer to learn something new and share your progress",
      "Keep a 'learning log' to track experiments and outcomes"
    ],
    high: [
      "Create structured learning moments in team meetings",
      "Start a 'Failure Friday' or learning retrospective practice",
      "Mentor someone who's learning a skill you've mastered",
      "Share resources and learning opportunities proactively",
      "Document and share your learning journey openly",
      "Champion a 'blameless post-mortem' culture",
      "Celebrate others' learning moments publicly"
    ],
    leveraging: [
      "Your comfort with learning helps normalize growth mindset for everyone",
      "You can make it safe for others to admit what they don't know",
      "Use your position to ask questions that others are afraid to ask",
      "Your vulnerability about learning can unlock others' honesty"
    ]
  },
  contributor: {
    low: [
      "Prepare one contribution to share in your next team meeting",
      "Offer your perspective on a decision even if not asked",
      "Identify one unique skill you can offer and share it with the team",
      "Speak up earlier in meetings rather than waiting",
      "Start with 'I'm not sure if this is right, but...' — it lowers the stakes"
    ],
    high: [
      "Actively draw out quieter colleagues' contributions",
      "Ask 'What do you think?' to others before sharing your view",
      "Champion others' ideas publicly and give credit generously",
      "Create space for contributions in meetings you facilitate",
      "Mentor others on how to share ideas effectively",
      "Advocate for diverse voices in decision-making processes",
      "Use your influence to amplify underrepresented perspectives"
    ],
    leveraging: [
      "Your voice carries weight - use it to create space for others",
      "When you contribute confidently, it signals safety to others",
      "You can model how to share ideas constructively and invite feedback",
      "Your comfort contributing means you can take risks others can't"
    ]
  },
  challenger: {
    low: [
      "Practice phrases like 'May I offer a different perspective?'",
      "Write down your concerns before a meeting so you're prepared to share",
      "Start small - challenge a low-stakes decision first",
      "Find an ally who can support your perspective",
      "Frame challenges as questions: 'Have we considered...?'"
    ],
    high: [
      "Model how to challenge respectfully and constructively",
      "Create explicit moments for dissent: 'What are we missing?'",
      "Thank people publicly who disagree with you",
      "Assign 'devil's advocate' roles to normalize challenging",
      "Coach others on how to challenge effectively",
      "Protect those who speak up from any negative consequences",
      "Share stories of when being challenged led to better outcomes"
    ],
    leveraging: [
      "Your courage to challenge is a gift to the team - use it wisely",
      "You can surface issues others see but are afraid to raise",
      "Model that disagreement and respect can coexist",
      "Use your comfort with conflict to hold space for healthy debate"
    ]
  }
};

export const REFLECTION_PROMPTS = {
  self: {
    title: "From Yourself",
    color: "#D4854A",
    prompts: {
      inclusion: [
        "What prevents me from fully showing up as myself with this team?",
        "When do I hold back from connecting with colleagues?",
        "What would help me feel more comfortable being authentic here?"
      ],
      learner: [
        "What am I afraid to admit I don't know?",
        "How do I typically respond when I make a mistake?",
        "What learning opportunity have I been avoiding?"
      ],
      contributor: [
        "What ideas have I held back from sharing? Why?",
        "What unique value do I bring that I'm not fully utilizing?",
        "When do I choose silence over contributing?"
      ],
      challenger: [
        "What concerns have I kept to myself recently?",
        "What's the worst that could happen if I spoke up?",
        "How can I frame challenges constructively?"
      ]
    }
  },
  teammates: {
    title: "From Your Team",
    color: "#6B9E9B",
    prompts: {
      inclusion: [
        "Who on the team could I get to know better?",
        "How could teammates help me feel more included?",
        "What small gestures from others would make a difference?"
      ],
      learner: [
        "Who could I learn from on this team?",
        "How could teammates respond differently when I make mistakes?",
        "What would make me feel safer asking questions?"
      ],
      contributor: [
        "How could teammates better invite my input?",
        "What acknowledgment would help me contribute more?",
        "Who could be an ally in amplifying my ideas?"
      ],
      challenger: [
        "How could teammates make it easier to disagree?",
        "Who could support me when I need to push back?",
        "What team norms would help everyone challenge more safely?"
      ]
    }
  },
  leader: {
    title: "From Your Leader",
    color: "#2D4F4D",
    prompts: {
      inclusion: [
        "What could my leader do to make me feel more valued?",
        "How could my leader better acknowledge my presence and contributions?",
        "What signals from leadership would help me feel I belong?"
      ],
      learner: [
        "How does my leader respond to mistakes? What would I prefer?",
        "What could my leader do to encourage more experimentation?",
        "How could my leader better support my development?"
      ],
      contributor: [
        "How could my leader create more space for my input?",
        "What would help me feel my contributions are valued?",
        "How could decision-making processes better include my voice?"
      ],
      challenger: [
        "What would my leader need to do for me to feel safe challenging?",
        "How does my leader currently respond to pushback?",
        "What would make disagreement with leadership feel safer?"
      ]
    }
  },
  coach: {
    title: "From a Coach",
    color: "#C07338",
    prompts: {
      inclusion: [
        "What patterns do I have around belonging that a coach could help me explore?",
        "What past experiences affect how included I feel now?",
        "How could a coach help me build stronger team connections?"
      ],
      learner: [
        "What beliefs about failure might be limiting me?",
        "How could a coach help me develop a growth mindset?",
        "What learning strategies would help me take more risks?"
      ],
      contributor: [
        "What holds me back from contributing fully?",
        "How could a coach help me find my voice?",
        "What skills would help me share ideas more effectively?"
      ],
      challenger: [
        "What fears around conflict could a coach help me work through?",
        "How could I develop more confidence in challenging others?",
        "What techniques would help me navigate disagreement constructively?"
      ]
    }
  }
};

// Score interpretation thresholds
export const SCORE_THRESHOLDS = {
  excellent: { min: 4.5, label: 'Excellent', color: '#2D4F4D', description: 'A strong, healthy pulse' },
  good: { min: 3.5, label: 'Good', color: '#6B9E9B', description: 'A steady pulse with solid foundations' },
  developing: { min: 2.5, label: 'Developing', color: '#D4854A', description: 'A pulse with room to grow' },
  attention: { min: 0, label: 'Needs Attention', color: '#C07338', description: 'A pulse that needs care — but awareness is the first step' }
};

export const getScoreInterpretation = (score) => {
  if (score >= SCORE_THRESHOLDS.excellent.min) return SCORE_THRESHOLDS.excellent;
  if (score >= SCORE_THRESHOLDS.good.min) return SCORE_THRESHOLDS.good;
  if (score >= SCORE_THRESHOLDS.developing.min) return SCORE_THRESHOLDS.developing;
  return SCORE_THRESHOLDS.attention;
};
